import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

import PlanPreview from './components/PlanPreview';
import PlanTemplates from './components/PlanTemplates';
import FeaturesBuilder from './components/FeaturesBuilder';
import PricingConfiguration from './components/PricingConfiguration';

const MembershipPlanBuilder = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isSuperAdmin, loading: authLoading } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [existingPlans, setExistingPlans] = useState([]);

  const [planData, setPlanData] = useState({
    planName: '',
    planCode: '',
    description: '',
    billingCycle: 'monthly',
    price: '',
    setupFee: '0',
    trialPeriodDays: '0',
    maxMembers: '',
    features: [],
    benefits: [],
    accessLevel: 'standard',
    classCredits: '',
    personalTrainingSessions: '0',
    isActive: true,
    isFeatured: false,
    displayOrder: '0'
  });

  useEffect(() => {
    if (!authLoading && (!user || (!isGymOwner && !isSuperAdmin))) {
      navigate('/member-dashboard');
    }
  }, [user, isGymOwner, isSuperAdmin, authLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && (isGymOwner || isSuperAdmin)) {
      loadExistingPlans();
    }
  }, [currentTenant, isGymOwner, isSuperAdmin]);

  const loadExistingPlans = async () => {
    try {
      const { data, error } = await supabase?.from('membership_plans')?.select('*')?.eq('tenant_id', currentTenant?.id)?.order('display_order', { ascending: true });

      if (error) throw error;
      setExistingPlans(data || []);
    } catch (err) {
      console.error('Failed to load existing plans:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setPlanData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleApplyTemplate = (template) => {
    setPlanData({
      ...planData,
      planName: template?.planName,
      description: template?.description,
      billingCycle: template?.billingCycle,
      price: template?.price?.toString(),
      setupFee: template?.setupFee?.toString(),
      trialPeriodDays: template?.trialPeriodDays?.toString(),
      features: template?.features || [],
      benefits: template?.benefits || [],
      accessLevel: template?.accessLevel,
      classCredits: template?.classCredits?.toString(),
      personalTrainingSessions: template?.personalTrainingSessions?.toString()
    });
    setStep(2);
  };

  const validateStep = (currentStep) => {
    switch (currentStep) {
      case 2:
        if (!planData?.planName?.trim()) {
          setError('Plan name is required');
          return false;
        }
        if (!planData?.planCode?.trim()) {
          setError('Plan code is required');
          return false;
        }
        if (!planData?.price || parseFloat(planData?.price) < 0) {
          setError('Valid price is required');
          return false;
        }
        break;
      case 3:
        if (planData?.features?.length === 0) {
          setError('At least one feature is required');
          return false;
        }
        break;
      default:
        break;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, 4));
      setError('');
    }
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 1));
    setError('');
  };

  const handleSavePlan = async () => {
    if (!validateStep(step)) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const planPayload = {
        tenant_id: currentTenant?.id,
        plan_name: planData?.planName,
        plan_code: planData?.planCode?.toUpperCase(),
        description: planData?.description,
        billing_cycle: planData?.billingCycle,
        price: parseFloat(planData?.price),
        setup_fee: parseFloat(planData?.setupFee || 0),
        trial_period_days: parseInt(planData?.trialPeriodDays || 0),
        max_members: planData?.maxMembers ? parseInt(planData?.maxMembers) : null,
        features: planData?.features,
        benefits: planData?.benefits,
        access_level: planData?.accessLevel,
        class_credits: planData?.classCredits ? parseInt(planData?.classCredits) : null,
        personal_training_sessions: parseInt(planData?.personalTrainingSessions || 0),
        is_active: planData?.isActive,
        is_featured: planData?.isFeatured,
        display_order: parseInt(planData?.displayOrder || 0),
        created_by: user?.id
      };

      const { data, error } = await supabase?.from('membership_plans')?.insert([planPayload])?.select();

      if (error) throw error;

      setSuccess('Membership plan created successfully!');
      setTimeout(() => {
        navigate('/membership-management-dashboard');
      }, 2000);
    } catch (err) {
      setError(err?.message || 'Failed to create membership plan');
      console.error('Plan creation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const billingCycleOptions = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly (3 months)' },
    { value: 'semi_annual', label: 'Semi-Annual (6 months)' },
    { value: 'annual', label: 'Annual (12 months)' }
  ];

  const accessLevelOptions = [
    { value: 'basic', label: 'Basic Access' },
    { value: 'standard', label: 'Standard Access' },
    { value: 'premium', label: 'Premium Access' },
    { value: 'elite', label: 'Elite Access' }
  ];

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <PlanTemplates
            onApplyTemplate={handleApplyTemplate}
            onSkip={() => setStep(2)}
            existingPlans={existingPlans}
          />
        );
      case 2:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Plan Name"
                placeholder="e.g., Premium Membership"
                value={planData?.planName}
                onChange={(e) => handleInputChange('planName', e?.target?.value)}
                required
              />
              <Input
                label="Plan Code"
                placeholder="e.g., PREMIUM"
                value={planData?.planCode}
                onChange={(e) => handleInputChange('planCode', e?.target?.value)}
                required
              />
            </div>
            <Input
              label="Description"
              placeholder="Describe what this plan offers..."
              value={planData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
            />
            <PricingConfiguration
              planData={planData}
              onUpdate={handleInputChange}
              billingCycleOptions={billingCycleOptions}
              accessLevelOptions={accessLevelOptions}
            />
          </div>
        );
      case 3:
        return (
          <FeaturesBuilder
            features={planData?.features}
            benefits={planData?.benefits}
            onUpdateFeatures={(features) => handleInputChange('features', features)}
            onUpdateBenefits={(benefits) => handleInputChange('benefits', benefits)}
          />
        );
      case 4:
        return (
          <PlanPreview
            planData={planData}
            onEdit={(field) => {
              if (field === 'pricing') setStep(2);
              if (field === 'features') setStep(3);
            }}
          />
        );
      default:
        return null;
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <Icon name="Loader2" size={48} className="animate-spin text-primary" />
        </div>
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6 pb-24 md:pb-6">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
              Membership Plan Builder
            </h1>
            <p className="text-muted-foreground">
              Create and customize membership plans for your gym
            </p>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/membership-management-dashboard')}
            iconName="X"
          >
            Cancel
          </Button>
        </div>

        {/* Progress Steps */}
        <div className="bg-card rounded-xl shadow-md p-6 mb-6">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4]?.map((stepNum) => (
              <React.Fragment key={stepNum}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                      step >= stepNum
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {stepNum}
                  </div>
                  <span className="text-xs text-muted-foreground mt-2">
                    {stepNum === 1 && 'Template'}
                    {stepNum === 2 && 'Details'}
                    {stepNum === 3 && 'Features'}
                    {stepNum === 4 && 'Preview'}
                  </span>
                </div>
                {stepNum < 4 && (
                  <div
                    className={`flex-1 h-1 mx-2 rounded transition-colors ${
                      step > stepNum ? 'bg-primary' : 'bg-muted'
                    }`}
                  ></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-destructive/10 border border-destructive rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
              <p className="text-destructive font-medium">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-success/10 border border-success rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" />
              <p className="text-success font-medium">{success}</p>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="bg-card rounded-xl shadow-md p-6 mb-6">
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={step === 1 || loading}
            iconName="ChevronLeft"
            iconPosition="left"
          >
            Back
          </Button>
          <div className="flex items-center gap-3">
            {step < 4 ? (
              <Button
                onClick={handleNext}
                disabled={loading}
                iconName="ChevronRight"
                iconPosition="right"
              >
                Next
              </Button>
            ) : (
              <Button
                onClick={handleSavePlan}
                loading={loading}
                iconName="Save"
                iconPosition="left"
              >
                Create Plan
              </Button>
            )}
          </div>
        </div>
      </main>
      <MobileBottomNav />
    </div>
  );
};

export default MembershipPlanBuilder;